/* ConnectorSetupModal — one modal that handles all 4 connector kinds:
 *   - shopify  : OAuth handoff (or paste shop domain + admin access token)
 *   - gsheet   : paste Google Sheet ID + gid (public-share path)
 *   - email    : show inbound alias + let seller pick the default profile
 *   - webhook  : generate per-connector token, show the POST URL
 *
 * Saving creates / updates a CatalogConnector for the seller. */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import {
  ShoppingBag, Cloud, AtSign, Globe, Copy, ExternalLink, Check,
} from "lucide-react";

const KIND_META = {
  shopify: { icon: ShoppingBag, label: "Shopify" },
  gsheet:  { icon: Cloud,       label: "Google Sheet" },
  email:   { icon: AtSign,      label: "Email-in" },
  webhook: { icon: Globe,       label: "Real-time webhook" },
};

const SCHEDULES = [
  { v: "manual",   l: "Manual only" },
  { v: "hourly",   l: "Every hour" },
  { v: "daily",    l: "Daily (default)" },
  { v: "realtime", l: "Real-time (push)" },
];

export default function ConnectorSetupModal({ kind, inboundEmail, profiles, existingConnector, onClose, onSaved }) {
  const meta = KIND_META[kind] || {};
  const Icon = meta.icon || Cloud;
  const isEdit = !!existingConnector;
  const [name, setName] = useState(existingConnector?.name || meta.label || "");
  const [profileId, setProfileId] = useState(existingConnector?.profile_id || "");
  const [schedule, setSchedule] = useState(existingConnector?.schedule || (kind === "webhook" || kind === "email" ? "realtime" : "daily"));
  const [enabled, setEnabled] = useState(existingConnector ? !!existingConnector.enabled : true);
  const [config, setConfig] = useState(existingConnector?.config || {});
  const [busy, setBusy] = useState(false);

  const aliasShort = useMemo(() => {
    // For email kind: use 8-char short from new tokens generator or existing config
    if (kind !== "email") return "";
    if (existingConnector?.config?.alias_short) return existingConnector.config.alias_short;
    return Math.random().toString(36).slice(2, 10);
  }, [kind, existingConnector]);

  const webhookToken = useMemo(() => {
    if (kind !== "webhook") return "";
    if (existingConnector?.config?.token) return existingConnector.config.token;
    return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  }, [kind, existingConnector]);

  const webhookUrl = useMemo(() => {
    const base = (typeof window !== "undefined" && window.location.origin) || "";
    return webhookToken ? `${base}/api/inbound/catalog/webhook/${webhookToken}` : "";
  }, [webhookToken]);

  const inboundAlias = useMemo(() => {
    if (kind !== "email") return "";
    if (existingConnector?.config?.alias_short) {
      // Existing alias = catalog-<existing-short>@verqo.in
      return inboundEmail.replace(/catalog-[a-z0-9]+/, `catalog-${existingConnector.config.alias_short}`);
    }
    return inboundEmail;
  }, [kind, existingConnector, inboundEmail]);

  const save = async () => {
    setBusy(true);
    let cfg = { ...config };
    if (kind === "email") { cfg.alias_short = aliasShort; cfg.alias = inboundAlias; }
    if (kind === "webhook") { cfg.token = webhookToken; }
    try {
      if (isEdit) {
        await api.put(`/seller/catalog-import/connectors/${existingConnector.id}`, {
          kind, name, profile_id: profileId || null, config: cfg, enabled, schedule,
        });
      } else {
        await api.post("/seller/catalog-import/connectors", {
          kind, name: name || meta.label, profile_id: profileId || null, config: cfg, enabled, schedule,
        });
      }
      toast.success(`${meta.label} ${isEdit ? "updated" : "set up"}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const copy = (s) => { navigator.clipboard.writeText(s); toast.success("Copied"); };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid={`connector-modal-${kind}`}>
        <DialogHeader>
          <DialogTitle className="display text-2xl tracking-tight inline-flex items-center gap-2">
            <Icon className="w-5 h-5 text-foreground/55"/>{meta.label}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? "Update connection settings" : "Set up a recurring import source"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="con-name">Name</Label>
            <Input id="con-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={meta.label} className="mt-1.5 h-10"/>
          </div>

          {/* Per-kind config */}
          {kind === "shopify" && <ShopifyConfig config={config} setConfig={setConfig}/>}
          {kind === "gsheet"  && <GSheetConfig  config={config} setConfig={setConfig}/>}
          {kind === "email"   && <EmailConfig   alias={inboundAlias} onCopy={copy}/>}
          {kind === "webhook" && <WebhookConfig url={webhookUrl} onCopy={copy}/>}

          <div>
            <Label htmlFor="con-profile">Default import profile (optional)</Label>
            <select id="con-profile" value={profileId} onChange={(e) => setProfileId(e.target.value)}
              className="mt-1.5 w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
              <option value="">— infer columns each time (slower) —</option>
              {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <p className="text-[11px] text-foreground/55 mt-1">A saved profile makes daily syncs cost $0 in AI.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="con-sched">Schedule</Label>
              <select id="con-sched" value={schedule} onChange={(e) => setSchedule(e.target.value)}
                className="mt-1.5 w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
                {SCHEDULES.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 cursor-pointer h-10">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="accent-foreground"/>
                <span className="text-sm">Enabled</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="connector-save"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90">
            {busy ? "Saving…" : isEdit ? "Update connector" : "Set up connector"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ShopifyConfig({ config, setConfig }) {
  return (
    <>
      <div>
        <Label htmlFor="sh-domain">Shop domain</Label>
        <Input id="sh-domain" value={config.shop_domain || ""}
          onChange={(e) => setConfig({ ...config, shop_domain: e.target.value })}
          placeholder="acme.myshopify.com" className="mt-1.5 h-10 mono text-sm"/>
      </div>
      <div>
        <Label htmlFor="sh-token">Admin API access token</Label>
        <Input id="sh-token" type="password" value={config.access_token || ""}
          onChange={(e) => setConfig({ ...config, access_token: e.target.value })}
          placeholder="shpat_…" className="mt-1.5 h-10 mono text-sm"/>
        <p className="text-[11px] text-foreground/55 mt-1">
          Or use <a className="underline" href="/seller/catalog/connect-shopify">1-click OAuth</a> for an easier setup.
        </p>
      </div>
    </>
  );
}

function GSheetConfig({ config, setConfig }) {
  return (
    <>
      <div>
        <Label htmlFor="gs-id">Google Sheet ID</Label>
        <Input id="gs-id" value={config.sheet_id || ""}
          onChange={(e) => setConfig({ ...config, sheet_id: e.target.value })}
          placeholder="1A2B3C…" className="mt-1.5 h-10 mono text-sm"/>
        <p className="text-[11px] text-foreground/55 mt-1">
          From your sheet URL: <span className="mono">docs.google.com/spreadsheets/d/<strong>this-part</strong>/edit</span>
        </p>
      </div>
      <div>
        <Label htmlFor="gs-gid">Tab GID (optional)</Label>
        <Input id="gs-gid" value={config.gid || ""}
          onChange={(e) => setConfig({ ...config, gid: e.target.value })}
          placeholder="0" className="mt-1.5 h-10 mono text-sm"/>
      </div>
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[11.5px] text-amber-900">
        Set the sheet to <strong>&quot;Anyone with the link can view&quot;</strong> for the simplest setup.
        For private sheets, ask your admin to set up the verqo importer service account.
      </div>
    </>
  );
}

function EmailConfig({ alias, onCopy }) {
  return (
    <div className="rounded-xl bg-foreground text-background p-5">
      <div className="text-[10px] mono uppercase tracking-[0.22em] opacity-65">Your private inbox</div>
      <div className="display text-lg mt-2">Forward your daily file here</div>
      <div className="mt-3 px-3 py-2.5 rounded-lg bg-white/10 text-sm mono inline-flex items-center gap-3">
        {alias}
        <button onClick={() => onCopy(alias)} className="text-accent inline-flex items-center gap-1 hover:underline">
          <Copy className="w-3 h-3"/>copy
        </button>
      </div>
      <p className="text-[12px] opacity-75 mt-3 leading-relaxed">
        Any CSV / Excel / PDF you email here will run through the selected profile and update your catalog automatically.
      </p>
    </div>
  );
}

function WebhookConfig({ url, onCopy }) {
  return (
    <div className="space-y-3">
      <div className="rounded-xl bg-secondary border border-border p-4">
        <Label>POST URL</Label>
        <div className="mt-1.5 flex items-center gap-2 rounded-md bg-white border border-border px-3 py-2 mono text-[11.5px] break-all">
          {url}
          <button onClick={() => onCopy(url)} className="ml-auto shrink-0 text-foreground/55 hover:text-foreground" title="Copy"><Copy className="w-3.5 h-3.5"/></button>
        </div>
        <p className="text-[11px] text-foreground/55 mt-2">
          POST <span className="mono">{`{"products":[{...}]}`}</span> here from your ERP — payloads accepted in real-time, no auth header needed (token is in the URL).
        </p>
      </div>
    </div>
  );
}
